/**
 * Forked from
 * https://github.com/reduxjs/redux-toolkit/blob/2b2f9b8e582660a064c1e5f29017ee1af517e351/packages/rtk-query-graphql-request-base-query/src/index.ts
 *
 * Reason: Needed to add `graphQlErrorMessage` to error response, in order to
 * intercept datahub/grappa 200 responses with error messages. The standard
 * `rtk-query-graphql-request-base-query` doesn't give us this ability.
 */
import { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { DocumentNode } from 'graphql';
import { ClientError, GraphQLClient } from 'graphql-request';
import { GraphQLClientRequestHeaders } from 'graphql-request/src/types';
type P = Parameters<GraphQLClient['request']>[0];
export type Document = P['document'];
export type Variables = P['variables'];
export type RequestHeaders = P['requestHeaders'];

export const elevioGraphqlRequestBaseQuery = (
  options:
    | { url: string; requestHeaders?: RequestHeaders }
    | { client: GraphQLClient },
): BaseQueryFn<
  { document: string | DocumentNode; variables?: any },
  unknown,
  Pick<ClientError, 'name' | 'message' | 'stack'> & { code: string },
  Partial<Pick<ClientError, 'request' | 'response'>>
> => {
  const client =
    'client' in options ? options.client : new GraphQLClient(options.url);
  if ('requestHeaders' in options) {
    client.setHeaders(options.requestHeaders as GraphQLClientRequestHeaders);
  }
  return async ({ document, variables }) => {
    try {
      return { data: await client.request(document, variables), meta: {} };
    } catch (error) {
      if (error instanceof ClientError) {
        const { name, message, stack, request, response } = error;
        const errorWithCode =
          response.errors?.find(
            (err) => err.extensions.code === 'UpgradeRequired',
          ) || response.errors?.find((err) => err.extensions.code);
        const graphQlErrorMessage = errorWithCode?.message;
        const graphQlErrorCode = errorWithCode?.extensions.code as string;

        return {
          error: {
            name,
            // Return the graphql message if one exists, otherwise use standard error message
            message: graphQlErrorMessage || message,
            stack,
            code: graphQlErrorCode,
          },
          meta: { request, response },
        };
      }
      throw error;
    }
  };
};
